import { faMagnifyingGlass } from '@fortawesome/sharp-solid-svg-icons';
import { SCInputString, SCLoading, SCView } from '@lib/sc-eone-ui';
import { TabGroup } from 'eone-ui/tab/tab';
import { useMemo, useState } from 'react';
import { METADATA_BROWSER_TID, type SObjectListItem } from './types';

const TID = `${METADATA_BROWSER_TID}.objects`;

type Tab = 'all' | 'standard' | 'custom';
const TABS: Tab[] = ['all', 'standard', 'custom'];

interface ObjectsPanelProps {
  objects: SObjectListItem[];
  loading: boolean;
  selected: string | null;
  onSelect: (o: SObjectListItem) => void;
}

export function ObjectsPanel({ objects, loading, selected, onSelect }: ObjectsPanelProps) {
  const [filter, setFilter] = useState('');
  const [tab, setTab] = useState<Tab>('all');

  const visible = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return objects.filter((o) => {
      if (tab === 'standard' && o.isCustom) return false;
      if (tab === 'custom' && !o.isCustom) return false;
      if (!f) return true;
      return o.entity.toLowerCase().includes(f) || o.name.toLowerCase().includes(f);
    });
  }, [objects, filter, tab]);

  return (
    <SCView flexCol className="sfmb-panel sfmb-objects" DataTestId={TID}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          padding: 4,
          borderBottom: '1px solid black',
        }}
      >
        <SCView flex justifyBetween itemsCenter>
          <SCView fontBold textSm>
            Objects
          </SCView>
          <SCView textXs textDesc>
            {objects.length} objects
          </SCView>
        </SCView>
        <SCInputString
          Value={filter}
          OnChange={setFilter}
          Placeholder="Filter objects…"
          Icon={faMagnifyingGlass}
          DataTestId={`${TID}.filter`}
        />
        <TabGroup
          Value={tab}
          OnChange={(v: string) => setTab(v as Tab)}
          DataTestId={`${TID}.tab`}
          Tabs={TABS.map((t) => ({ Value: t, Label: t[0].toUpperCase() + t.slice(1) }))}
        />
      </div>

      <SCView flex1 overflowYAuto className="sfmb-list">
        {loading && <SCLoading />}
        {!loading && visible.length === 0 && (
          <SCView p={3} textDesc>
            No objects match.
          </SCView>
        )}
        {!loading &&
          visible.map((o) => (
            <button
              key={o.entity}
              type="button"
              className={`sfmb-obj-row ${selected === o.entity ? 'is-selected' : ''}`}
              onClick={() => onSelect(o)}
              title={o.entity}
              data-testid={`${TID}.item.${o.entity}`}
            >
              <SCView flex justifyBetween itemsCenter gap={2}>
                <SCView flexCol style={{ minWidth: 0 }}>
                  <SCView fontBold truncate>
                    {o.name}
                  </SCView>
                  <SCView textXs textDesc truncate className="sfmb-mono">
                    {o.entity}
                  </SCView>
                </SCView>
                {o.isCustom && <span className="sfmb-badge sfmb-badge-custom">CUSTOM</span>}
              </SCView>
            </button>
          ))}
      </SCView>
    </SCView>
  );
}
